(() => {
  const STORAGE_KEY = 'corail:splash-seen';
  const SPLASH_MODES = Object.freeze({
    FULL: 'full',
    REFRESH: 'refresh',
    NONE: 'none',
  });
  const FULL_SPLASH_DURATION_MS = 1900;
  const REFRESH_SPLASH_DURATION_MS = 650;
  const REDUCED_MOTION_DURATION_MS = 200;
  const FULL_EXIT_DURATION_MS = 320;
  const REFRESH_EXIT_DURATION_MS = 180;
  const REDUCED_MOTION_EXIT_DURATION_MS = 150;

  const CORAIL_LIQUID_CHROME = {
    baseColor: [1, 0.9804, 0.949],
    accentColor: [0.949, 0.4863, 0.1765],
    speed: 0.46,
    amplitude: 0.34,
    frequencyX: 2.4,
    frequencyY: 1.7,
    antialias: true,
    renderScale: 1,
  };

  const VERTEX_SHADER = `
    attribute vec2 position;
    attribute vec2 uv;
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = vec4(position, 0.0, 1.0);
    }
  `;

  // Ported verbatim from LiquidChrome.tsx. Only the colour uniforms differ.
  const FRAGMENT_SHADER = `
    precision highp float;
    uniform float uTime;
    uniform vec3 uResolution;
    uniform vec3 uBaseColor;
    uniform vec3 uAccentColor;
    uniform float uAmplitude;
    uniform float uFrequencyX;
    uniform float uFrequencyY;
    uniform vec2 uMouse;
    varying vec2 vUv;

    vec4 renderImage(vec2 uvCoord) {
      vec2 fragCoord = uvCoord * uResolution.xy;
      vec2 uv = (2.0 * fragCoord - uResolution.xy) / min(uResolution.x, uResolution.y);

      for (float i = 1.0; i < 10.0; i++) {
        uv.x += uAmplitude / i * cos(i * uFrequencyX * uv.y + uTime + uMouse.x * 3.14159);
        uv.y += uAmplitude / i * cos(i * uFrequencyY * uv.x + uTime + uMouse.y * 3.14159);
      }

      vec2 diff = (uvCoord - uMouse);
      float dist = length(diff);
      float falloff = exp(-dist * 20.0);
      float ripple = sin(10.0 * dist - uTime * 2.0) * 0.03;
      uv += (diff / (dist + 0.0001)) * ripple * falloff;

      float wave = max(abs(sin(uTime - uv.y - uv.x)), 0.08);
      float accentStrength = 0.5 + 0.5 * sin((uv.x * 1.7 + uv.y * 1.3) + uTime * 0.25);
      accentStrength = smoothstep(0.2, 0.85, accentStrength);
      vec3 blendedColor = mix(uBaseColor, uAccentColor, accentStrength);
      vec3 color = blendedColor / wave;

      return vec4(color, 1.0);
    }

    void main() {
      vec4 col = vec4(0.0);
      int samples = 0;

      for (int i = -1; i <= 1; i++) {
        for (int j = -1; j <= 1; j++) {
          vec2 offset = vec2(float(i), float(j)) * (1.0 / min(uResolution.x, uResolution.y));
          col += renderImage(vUv + offset);
          samples++;
        }
      }

      gl_FragColor = col / float(samples);
    }
  `;

  class LiquidChrome {
    constructor(container, options) {
      this.container = container;
      this.options = options;
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.frameId = null;
      this.resizeObserver = null;
      this.contextLost = false;
      this.destroyed = false;
      this.mouse = new Float32Array([0, 0]);
      this.resolution = new Float32Array([1, 1, 1]);
      this.baseColor = new Float32Array(options.baseColor);
      this.accentColor = new Float32Array(options.accentColor);

      this.renderFrame = this.renderFrame.bind(this);
      this.resize = this.resize.bind(this);
      this.handleContextLost = this.handleContextLost.bind(this);
    }

    mount({ animate }) {
      try {
        this.gl = this.canvas.getContext('webgl', {
          alpha: false,
          antialias: this.options.antialias,
          powerPreference: 'high-performance',
        });

        if (!this.gl) return false;

        this.createProgram();
        this.createGeometry();
        this.canvas.addEventListener('webglcontextlost', this.handleContextLost, false);
        this.container.append(this.canvas);
        this.resize();

        window.addEventListener('resize', this.resize);

        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(this.resize);
          this.resizeObserver.observe(this.container);
        }

        // Render once synchronously so the short refresh splash has Liquid Chrome immediately.
        this.draw(0);

        if (animate) this.frameId = window.requestAnimationFrame(this.renderFrame);

        return true;
      } catch (error) {
        this.destroy();
        return false;
      }
    }

    createProgram() {
      const gl = this.gl;
      const vertex = this.compileShader(gl.VERTEX_SHADER, VERTEX_SHADER);
      const fragment = this.compileShader(gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      const program = gl.createProgram();

      if (!program) throw new Error('Unable to create the Liquid Chrome WebGL program.');

      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);

      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || 'Unable to link the Liquid Chrome WebGL program.');
      }

      this.program = program;
      this.vertexShader = vertex;
      this.fragmentShader = fragment;
      this.attributes = {
        position: gl.getAttribLocation(program, 'position'),
        uv: gl.getAttribLocation(program, 'uv'),
      };
      this.uniforms = {
        time: gl.getUniformLocation(program, 'uTime'),
        resolution: gl.getUniformLocation(program, 'uResolution'),
        baseColor: gl.getUniformLocation(program, 'uBaseColor'),
        accentColor: gl.getUniformLocation(program, 'uAccentColor'),
        amplitude: gl.getUniformLocation(program, 'uAmplitude'),
        frequencyX: gl.getUniformLocation(program, 'uFrequencyX'),
        frequencyY: gl.getUniformLocation(program, 'uFrequencyY'),
        mouse: gl.getUniformLocation(program, 'uMouse'),
      };

      if (this.attributes.position < 0 || this.attributes.uv < 0 || Object.values(this.uniforms).some((value) => !value)) {
        throw new Error('Unable to initialise the Liquid Chrome shader uniforms.');
      }
    }

    compileShader(type, source) {
      const shader = this.gl.createShader(type);

      if (!shader) throw new Error('Unable to create a Liquid Chrome shader.');

      this.gl.shaderSource(shader, source);
      this.gl.compileShader(shader);

      if (!this.gl.getShaderParameter(shader, this.gl.COMPILE_STATUS)) {
        throw new Error(this.gl.getShaderInfoLog(shader) || 'Unable to compile a Liquid Chrome shader.');
      }

      return shader;
    }

    createGeometry() {
      const gl = this.gl;
      this.positionBuffer = gl.createBuffer();
      this.uvBuffer = gl.createBuffer();

      if (!this.positionBuffer || !this.uvBuffer) {
        throw new Error('Unable to create the Liquid Chrome geometry.');
      }

      // Matches OGL's Triangle geometry: one oversized triangle covers the viewport.
      gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 2, 0, 0, 2]), gl.STATIC_DRAW);
    }

    resize() {
      if (this.destroyed || !this.gl) return;

      const width = Math.max(this.container.offsetWidth, 1);
      const height = Math.max(this.container.offsetHeight, 1);
      const scale = Math.min(Math.max(this.options.renderScale, 0.25), 1);
      const renderWidth = Math.max(1, Math.round(width * scale));
      const renderHeight = Math.max(1, Math.round(height * scale));

      if (this.canvas.width !== renderWidth || this.canvas.height !== renderHeight) {
        this.canvas.width = renderWidth;
        this.canvas.height = renderHeight;
        this.gl.viewport(0, 0, renderWidth, renderHeight);
      }

      this.resolution[0] = renderWidth;
      this.resolution[1] = renderHeight;
      this.resolution[2] = renderWidth / renderHeight;
    }

    renderFrame(time) {
      if (this.destroyed || this.contextLost) return;

      this.frameId = window.requestAnimationFrame(this.renderFrame);
      this.draw(time * 0.001 * this.options.speed);
    }

    draw(time) {
      if (this.destroyed || !this.gl || this.contextLost) return;

      const gl = this.gl;
      gl.clearColor(1, 1, 1, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(this.program);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
      gl.enableVertexAttribArray(this.attributes.position);
      gl.vertexAttribPointer(this.attributes.position, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, this.uvBuffer);
      gl.enableVertexAttribArray(this.attributes.uv);
      gl.vertexAttribPointer(this.attributes.uv, 2, gl.FLOAT, false, 0, 0);

      gl.uniform1f(this.uniforms.time, time);
      gl.uniform3fv(this.uniforms.resolution, this.resolution);
      gl.uniform3fv(this.uniforms.baseColor, this.baseColor);
      gl.uniform3fv(this.uniforms.accentColor, this.accentColor);
      gl.uniform1f(this.uniforms.amplitude, this.options.amplitude);
      gl.uniform1f(this.uniforms.frequencyX, this.options.frequencyX);
      gl.uniform1f(this.uniforms.frequencyY, this.options.frequencyY);
      gl.uniform2fv(this.uniforms.mouse, this.mouse);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    stop() {
      if (this.frameId !== null) {
        window.cancelAnimationFrame(this.frameId);
        this.frameId = null;
      }
    }

    handleContextLost(event) {
      event.preventDefault();
      this.contextLost = true;
      this.stop();
      this.container.classList.remove('corail-splash__liquid--webgl');
      this.canvas.remove();
    }

    destroy() {
      if (this.destroyed) return;

      this.destroyed = true;
      this.stop();
      window.removeEventListener('resize', this.resize);
      this.resizeObserver?.disconnect();
      this.canvas.removeEventListener('webglcontextlost', this.handleContextLost, false);
      this.container.classList.remove('corail-splash__liquid--webgl');
      this.canvas.remove();

      if (!this.gl || this.contextLost) return;

      if (this.positionBuffer) this.gl.deleteBuffer(this.positionBuffer);
      if (this.uvBuffer) this.gl.deleteBuffer(this.uvBuffer);
      if (this.program) this.gl.deleteProgram(this.program);
      if (this.vertexShader) this.gl.deleteShader(this.vertexShader);
      if (this.fragmentShader) this.gl.deleteShader(this.fragmentShader);
      this.gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  }

  const root = document.documentElement;
  const splash = document.getElementById('corail-splash');
  const liquidContainer = splash?.querySelector('.corail-splash__liquid');

  if (!splash) return;

  let liquidChrome = null;

  const getNavigationType = () => {
    const navigationEntries = window.performance?.getEntriesByType?.('navigation');

    if (navigationEntries?.length) return navigationEntries[0].type;

    // Only use the deprecated API in browsers without Navigation Timing Level 2.
    if (window.performance?.navigation?.type === 1) return 'reload';

    return 'navigate';
  };

  const getSplashMode = (hasSeenSplash) => {
    if (!hasSeenSplash) return SPLASH_MODES.FULL;

    return getNavigationType() === 'reload' ? SPLASH_MODES.REFRESH : SPLASH_MODES.NONE;
  };

  const removeSplash = () => {
    liquidChrome?.destroy();
    root.classList.remove('corail-splash-pending', 'corail-splash-active', 'corail-splash-closing');
    root.removeAttribute('data-corail-splash-mode');
    splash.remove();
  };

  let hasSeenSplash;

  try {
    hasSeenSplash = window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch (error) {
    // Keep the page usable when sessionStorage is unavailable.
    removeSplash();
    return;
  }

  const splashMode = getSplashMode(hasSeenSplash);

  if (splashMode === SPLASH_MODES.NONE) {
    removeSplash();
    return;
  }

  // Mark the full opening as seen at launch so a reload during the animation uses refresh mode.
  if (splashMode === SPLASH_MODES.FULL) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, '1');
    } catch (error) {
      // The splash remains functional even if the flag cannot be persisted.
    }
  }

  root.setAttribute('data-corail-splash-mode', splashMode);
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  const isRefresh = splashMode === SPLASH_MODES.REFRESH;
  const duration = reduceMotion
    ? REDUCED_MOTION_DURATION_MS
    : isRefresh
      ? REFRESH_SPLASH_DURATION_MS
      : FULL_SPLASH_DURATION_MS;
  const exitDuration = reduceMotion
    ? REDUCED_MOTION_EXIT_DURATION_MS
    : isRefresh
      ? REFRESH_EXIT_DURATION_MS
      : FULL_EXIT_DURATION_MS;

  if (liquidContainer instanceof HTMLElement) {
    liquidChrome = new LiquidChrome(liquidContainer, CORAIL_LIQUID_CHROME);

    if (liquidChrome.mount({ animate: !reduceMotion })) {
      liquidContainer.classList.add('corail-splash__liquid--webgl');
    } else {
      liquidChrome = null;
    }
  }

  root.classList.add('corail-splash-active');

  window.setTimeout(() => {
    liquidChrome?.stop();
    root.classList.add('corail-splash-closing');
    window.setTimeout(removeSplash, exitDuration);
  }, duration);
})();
